/**
 * „Als PDF exportieren“: ein Blatt mit wenigen Entscheidungen, danach das
 * Druckfenster des Systems. Die Auswahl wird gemerkt – beim naechsten Mal
 * reicht ein Tipp.
 */
import { button, h } from '../../ui/dom.ts';
import { toast } from '../../ui/toast.ts';
import { hasOwnLyrics } from '../prompter-text.ts';
import { listReleases, listSongs, releaseKindLabel } from '../../db/repo.ts';
import type { Project, Release, Setlist, Song } from '../../types.ts';
import { printPdf } from './print.ts';
import type { PdfContent, PdfDocument, PdfOptions } from './print.ts';

const KEY = 'leadsheet:pdf-options';

const CONTENTS: { value: PdfContent; label: string; hint: string }[] = [
  { value: 'sheet', label: 'Mit Akkorden', hint: 'Das Lead Sheet, so wie es in der App steht.' },
  { value: 'lyrics', label: 'Nur Songtext', hint: 'Der Text ohne Akkorde, in Strophen gegliedert.' },
  { value: 'both', label: 'Beides', hint: 'Erst das Lead Sheet, darunter der Songtext – bei langen Lead Sheets auf eigener Seite.' },
];

function loadOptions(): PdfOptions {
  const fallback: PdfOptions = { content: 'sheet', notes: true, photos: true };
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) ?? 'null') as Partial<PdfOptions> | null;
    if (!raw) return fallback;
    return {
      content: CONTENTS.some((c) => c.value === raw.content) ? (raw.content as PdfContent) : fallback.content,
      notes: raw.notes ?? fallback.notes,
      photos: raw.photos ?? fallback.photos,
    };
  } catch {
    return fallback;
  }
}

function saveOptions(opts: PdfOptions): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(opts));
  } catch {
    /* privater Modus – dann eben nicht gemerkt */
  }
}

function checkRow(label: string, checked: boolean, onChange: (value: boolean) => void): HTMLElement {
  const input = h('input', { attrs: { type: 'checkbox' } });
  input.checked = checked;
  input.addEventListener('change', () => onChange(input.checked));
  return h('label', { class: 'pdf-dialog__check' }, input, h('span', { text: label }));
}

export function openPdfDialog(doc: PdfDocument): void {
  if (doc.songs.length === 0) {
    toast('Keine Songs zum Exportieren');
    return;
  }
  const opts = loadOptions();

  const hint = h('p', { class: 'hint' });
  const lyricsNote = h('p', { class: 'hint' });
  const seg = h('div', { class: 'segmented', attrs: { role: 'radiogroup', 'aria-label': 'Inhalt' } });

  function paint(): void {
    seg.textContent = '';
    for (const c of CONTENTS) {
      seg.append(
        h('button', {
          class: 'segmented__item' + (c.value === opts.content ? ' segmented__item--on' : ''),
          text: c.label,
          attrs: { type: 'button', role: 'radio', 'aria-checked': String(c.value === opts.content) },
          on: {
            click: () => {
              opts.content = c.value;
              paint();
            },
          },
        }),
      );
    }
    hint.textContent = CONTENTS.find((c) => c.value === opts.content)?.hint ?? '';
    const withoutLyrics = doc.songs.filter((s) => !hasOwnLyrics(s)).length;
    lyricsNote.hidden = opts.content === 'sheet' || withoutLyrics === 0;
    lyricsNote.textContent =
      withoutLyrics === doc.songs.length
        ? 'Ohne eigenes Songtext-Feld wird der Text aus dem Lead Sheet genommen.'
        : `${withoutLyrics} von ${doc.songs.length} Songs haben kein eigenes Songtext-Feld – dort kommt der Text aus dem Lead Sheet.`;
  }
  paint();

  const count = doc.songs.length;
  const modal = h(
    'div',
    { class: 'modal', attrs: { role: 'dialog', 'aria-modal': 'true' } },
    h(
      'div',
      { class: 'modal__panel pdf-dialog' },
      h(
        'div',
        { class: 'modal__head' },
        h('h2', { class: 'modal__title', text: 'Als PDF exportieren' }),
        button('Abbrechen', () => modal.remove(), { class: 'btn btn--small' }),
      ),
      h(
        'div',
        { class: 'modal__body' },
        h('p', { class: 'pdf-dialog__what', text: `${doc.title || 'Ohne Titel'} · ${count} ${count === 1 ? 'Song' : 'Songs'}` }),
        h('div', { class: 'field' }, h('span', { class: 'field__label', text: 'Inhalt' }), seg, hint, lyricsNote),
        h(
          'div',
          { class: 'field' },
          h('span', { class: 'field__label', text: 'Dazu' }),
          checkRow('Notizen', opts.notes, (v) => (opts.notes = v)),
          checkRow('Fotos der Lead Sheets', opts.photos, (v) => (opts.photos = v)),
        ),
        h('p', {
          class: 'hint',
          text: 'Danach öffnet sich das Druckfenster. Dort „Als PDF sichern“ wählen – auf dem iPad oben über Teilen → „In Dateien sichern“.',
        }),
      ),
      h(
        'div',
        { class: 'modal__foot' },
        button(
          'PDF erstellen',
          () => {
            saveOptions(opts);
            modal.remove();
            // Ohne Umweg, solange der Tipp noch frisch ist – Safari erlaubt
            // das Druckfenster nur direkt nach einer Beruehrung.
            void printPdf(doc, { ...opts });
          },
          { class: 'btn btn--primary' },
        ),
      ),
    ),
  );
  modal.addEventListener('click', (ev) => {
    if (ev.target === modal) modal.remove();
  });
  document.body.append(modal);
}

/* ------------------------------------------------------- Einstiege */

async function songsByIds(ids: string[], projectId: string): Promise<Song[]> {
  const byId = new Map((await listSongs(projectId)).map((s) => [s.id, s]));
  return ids.map((id) => byId.get(id)).filter((s): s is Song => !!s);
}

export function exportSong(song: Song): void {
  openPdfDialog({ kind: 'Song', title: song.title || 'Ohne Titel', songs: [song] });
}

export async function exportRelease(release: Release): Promise<void> {
  openPdfDialog({
    kind: releaseKindLabel(release.kind),
    title: release.title || 'Ohne Titel',
    subtitle: (release.year ?? '').trim() || undefined,
    artwork: release.artwork,
    notes: release.notes,
    songs: await songsByIds(release.songIds, release.projectId),
  });
}

export async function exportSetlist(setlist: Setlist): Promise<void> {
  openPdfDialog({
    kind: 'Setlist',
    title: setlist.name || 'Setlist',
    notes: setlist.notes,
    songs: await songsByIds(setlist.songIds, setlist.projectId),
  });
}

/** Ganzes Projekt: erst die Songs der Alben/EPs in ihrer Reihenfolge, dann der Rest nach Titel. */
export async function exportProject(project: Project): Promise<void> {
  const [songs, releases] = await Promise.all([listSongs(project.id), listReleases(project.id)]);
  const byId = new Map(songs.map((s) => [s.id, s]));
  const ordered: Song[] = [];
  const seen = new Set<string>();
  for (const release of [...releases].sort((a, b) => a.order - b.order)) {
    for (const id of release.songIds) {
      const song = byId.get(id);
      if (song && !seen.has(id)) {
        ordered.push(song);
        seen.add(id);
      }
    }
  }
  const rest = songs.filter((s) => !seen.has(s.id)).sort((a, b) => (a.title || '').localeCompare(b.title || '', 'de'));
  openPdfDialog({
    kind: 'Projekt',
    title: project.name || 'Ohne Namen',
    subtitle: (project.subtitle ?? '').trim() || undefined,
    artwork: project.coverImage,
    songs: [...ordered, ...rest],
  });
}
